/**
 * Der Kopf: Orientierung oben, Arbeitsablauf darunter.
 *
 * Oben steht nicht noch mehr Navigation, sondern wo man gerade ist und was als
 * Nächstes fällig ist — „Zweite Augusthälfte 2026 · 8 Tage warten auf
 * Freigabe", mit Link dorthin. Darunter nur die Schritte der täglichen Arbeit.
 * Audit und Stammdaten sind wichtig, aber keine tägliche Arbeit: sie liegen
 * unter „Mehr". Konto, Darstellung und Abmelden liegen im Kontomenü; ein
 * einzelnes „Hell" im Kopf wirkte technisch und lenkte ab.
 *
 * Die aktive Seite ist unterstrichen, nicht hinterlegt: ein Kasten sah aus wie
 * ein Knopf, nicht wie „hier bin ich".
 *
 * Unter 1024 px liegt die Navigation hinter „Menü", statt in weitere Zeilen
 * umzubrechen.
 */
import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import type { WorkflowDto } from "../lib/api";
import { canReview, canWrite, type AuthMode, type MeUser } from "../lib/auth";
import { Menu } from "./Menu";
import { NivaroLogo } from "./NivaroLogo";
import { hilfeThemaFuer } from "../lib/hilfe";

interface NavItem {
  to: string;
  label: string;
  end?: boolean;
  /** Nur ausblenden, was die Rolle ohnehin nicht darf — die API weist es selbst ab. */
  darf?: (u: MeUser) => boolean;
}

/** Die tägliche Arbeit, in der Reihenfolge, in der man sie tut. */
const ARBEIT: NavItem[] = [
  { to: "/", label: "Übersicht", end: true },
  { to: "/import", label: "Import", darf: canWrite },
  { to: "/kassen-review", label: "Kassen klären", darf: canReview },
  { to: "/betriebstage", label: "Tage freigeben" },
  { to: "/auszahlungen", label: "Auszahlen", darf: canWrite },
  { to: "/monat", label: "Monatsübersicht" },
];
/** Wichtig, aber nicht täglich. */
const MEHR: NavItem[] = [
  { to: "/auswertung", label: "Auswertung" },
  { to: "/audit", label: "Audit" },
  { to: "/stammdaten", label: "Stammdaten" },
];

const navClass = ({ isActive }: { isActive: boolean }) =>
  `nav-link ${isActive ? "nav-link-active" : ""}`;
const menuItemClass = ({ isActive }: { isActive: boolean }) =>
  `menu-item ${isActive ? "menu-item-active" : ""}`;

export function AppHeader({
  user,
  mode,
  workflow,
  onLogout,
  loggingOut,
}: {
  user: MeUser;
  mode: AuthMode;
  workflow: WorkflowDto | undefined;
  onLogout: () => void;
  loggingOut: boolean;
}) {
  const { pathname } = useLocation();
  const arbeit = ARBEIT.filter((i) => !i.darf || i.darf(user));
  const inMehr = MEHR.some((i) => pathname.startsWith(i.to));

  /*
   * Nur ein Zähler, und nur wenn es etwas zu klären gibt. Er zählt den
   * laufenden Abrechnungszeitraum, nicht den ganzen Bestand — ein Alarm, der
   * auf nichts zeigt, wird nach zwei Tagen ignoriert.
   */
  const offeneKassen = workflow?.schritte.find((s) => s.id === "review")?.anzahl ?? 0;
  const zaehler = (to: string) =>
    to === "/kassen-review" && offeneKassen > 0 ? (
      <span
        className="chip-alert"
        title={`${offeneKassen} im laufenden Abrechnungszeitraum. Ältere stehen in der Liste darunter.`}
      >
        {offeneKassen}
      </span>
    ) : null;

  return (
    <header className="sticky top-0 z-20 border-b border-line bg-page/95 backdrop-blur">
      <div className="mx-auto max-w-[1400px] px-6">
        <div className="flex items-center gap-4 py-3">
          <Link to="/" className="flex items-center gap-3">
            {/* Auf dem Handy reicht das Zeichen; das ganze Logo nähme „Menü" den Platz. */}
            <img src="/icons/icon-192.png" alt="Nivaro" className="h-7 w-7 rounded sm:hidden" />
            <NivaroLogo className="hidden h-6 sm:block" />
            <span className="hidden h-5 w-px bg-line sm:block" aria-hidden />
            <span className="font-display text-lg font-medium tracking-tight">
              Trinkgeld<span className="text-accent">.</span>
            </span>
          </Link>

          <Orientierung workflow={workflow} />

          <div className="ml-auto flex items-center gap-2">
            {mode === "dev" ? (
              <span className="chip-warn hidden sm:inline-flex" title="DEV_AUTH_BYPASS aktiv, nur lokal erlaubt">
                Entwicklungsmodus
              </span>
            ) : null}

            {/* Schmale Bildschirme: die ganze Navigation hinter einem Knopf. */}
            <div className="lg:hidden">
              <Menu label="Menü" buttonClassName="nav-link border-b-0" panelClassName="w-64">
                {() => (
                  <>
                    {arbeit.map((i) => (
                      <NavLink key={i.to} to={i.to} end={i.end} className={menuItemClass} role="menuitem">
                        {i.label}
                        {zaehler(i.to)}
                      </NavLink>
                    ))}
                    <div className="my-1 border-t border-line" />
                    {MEHR.map((i) => (
                      <NavLink key={i.to} to={i.to} className={menuItemClass} role="menuitem">
                        {i.label}
                      </NavLink>
                    ))}
                  </>
                )}
              </Menu>
            </div>

            {/* Von jeder Seite aus erreichbar, und gleich beim passenden Thema. */}
            <Link
              to={`/hilfe#${hilfeThemaFuer(pathname)}`}
              className={`nav-link border-b-0 ${pathname.startsWith("/hilfe") ? "nav-link-active" : ""}`}
              title="Hilfe zu dieser Seite"
            >
              <span aria-hidden className="mr-1 inline-flex h-4 w-4 items-center justify-center rounded-full border border-current text-[10px] leading-none">
                ?
              </span>
              <span className="hidden sm:inline">Hilfe</span>
            </Link>

            <KontoMenu user={user} mode={mode} onLogout={onLogout} loggingOut={loggingOut} />
          </div>
        </div>

        <nav className="-mb-px hidden items-center gap-1 lg:flex" aria-label="Arbeitsablauf">
          {arbeit.map((i) => (
            <NavLink key={i.to} to={i.to} end={i.end} className={navClass}>
              {i.label}
              {zaehler(i.to)}
            </NavLink>
          ))}
          <Menu label="Mehr" align="left" active={inMehr} buttonClassName="nav-link">
            {() =>
              MEHR.map((i) => (
                <NavLink key={i.to} to={i.to} className={menuItemClass} role="menuitem">
                  {i.label}
                </NavLink>
              ))
            }
          </Menu>
        </nav>
      </div>
    </header>
  );
}

/**
 * Wo man gerade steht und was als Nächstes dran ist — derselbe Schritt, den
 * die Übersicht hervorhebt, damit Kopf und Übersicht nie Verschiedenes sagen.
 */
function Orientierung({ workflow }: { workflow: WorkflowDto | undefined }) {
  const periode = workflow?.periode;
  if (!periode) return null;
  const aktiv = workflow.schritte.find((s) => s.status === "aktiv");
  const zeitraum = periode.label.charAt(0).toUpperCase() + periode.label.slice(1);
  // Unter 1280 px reicht der Platz nicht für beides ausgeschrieben; dann das
  // Datum statt „Zweite August…" — abgeschnittene Wörter liest niemand.
  const kurz = `${periode.from.slice(8)}.–${periode.to.slice(8)}.${periode.to.slice(5, 7)}.`;
  const ende = `${periode.to.slice(8)}.${periode.to.slice(5, 7)}.`;
  // Dieselben Zustände wie die Karte auf der Übersicht. „alles erledigt“ gibt
  // es nicht mehr: nach einer fertigen Hälfte ist die nächste dran.
  const [text, ziel, ton] =
    workflow.stand === "neu"
      ? ["neue Abrechnung starten", "/import", "text-accent"]
      : workflow.stand === "laeuft_noch"
        ? [`läuft noch bis ${ende}`, "/", "text-muted"]
        : aktiv
          ? [aktiv.text, aktiv.ziel, "text-accent"]
          : ["abgeschlossen", "/", "text-ok"];

  return (
    <Link
      to={ziel}
      className="hidden min-w-0 items-baseline gap-2 truncate rounded-sm px-2 py-1 text-sm hover:bg-surface md:flex"
      title={
        workflow.stand === "neu"
          ? "Die Hälfte davor ist abgeschlossen. Nächster Schritt: Dateien einlesen"
          : aktiv
            ? `Nächster Schritt: ${aktiv.titel}`
            : undefined
      }
    >
      <span className="hidden whitespace-nowrap text-ink xl:inline">{zeitraum}</span>
      <span className="whitespace-nowrap text-ink xl:hidden">{kurz}</span>
      <span className="text-muted">·</span>
      <span className={`truncate ${ton}`}>{text}</span>
    </Link>
  );
}

function KontoMenu({
  user,
  mode,
  onLogout,
  loggingOut,
}: {
  user: MeUser;
  mode: AuthMode;
  onLogout: () => void;
  loggingOut: boolean;
}) {
  const [theme, toggleTheme] = useTheme();
  const name = user.displayName ?? "Konto";

  return (
    <Menu
      label={<span className="inline-block max-w-[7rem] truncate align-bottom sm:max-w-[12rem]">{name}</span>}
      title="Konto, Darstellung, Abmelden"
      buttonClassName="nav-link border-b-0 font-sans text-sm normal-case tracking-normal text-ink"
    >
      {(close) => (
        <>
          <div className="px-3 pb-2 pt-2">
            <div className="text-sm text-ink">{name}</div>
            <div className="text-xs text-muted">
              {user.roleLabel}
              {user.username ? ` · ${user.username}` : ""}
            </div>
          </div>
          <div className="my-1 border-t border-line" />
          <NavLink
            to="/konto"
            className={(a) => `${menuItemClass(a)} flex-col items-start gap-0`}
            role="menuitem"
          >
            Mein Konto
            <span className="text-xs text-muted">Passwort, zweiter Faktor</span>
          </NavLink>
          <button
            type="button"
            className="menu-item"
            role="menuitem"
            onClick={() => {
              toggleTheme();
              close();
            }}
          >
            {theme === "dark" ? "Helle Darstellung" : "Dunkle Darstellung"}
          </button>
          {mode !== "dev" ? (
            <>
              <div className="my-1 border-t border-line" />
              <button
                type="button"
                className="menu-item"
                role="menuitem"
                disabled={loggingOut}
                onClick={onLogout}
              >
                Abmelden
              </button>
            </>
          ) : null}
        </>
      )}
    </Menu>
  );
}

/**
 * Hell oder dunkel. Ohne eigene Wahl folgt die App dem System; angezeigt wird
 * immer, was gerade tatsächlich gilt — vorher stand „Dunkel" auf dem
 * Schalter, auch wenn das System schon dunkel war.
 */
function useTheme(): ["light" | "dark", () => void] {
  const system = (): "light" | "dark" =>
    window.matchMedia?.("(prefers-color-scheme: dark)").matches ? "dark" : "light";
  const [theme, setTheme] = useState<"light" | "dark">(() => {
    try {
      const stored = localStorage.getItem("theme");
      if (stored === "light" || stored === "dark") return stored;
    } catch {
      // Privater Modus: Systemeinstellung.
    }
    return system();
  });

  useEffect(() => {
    try {
      const stored = localStorage.getItem("theme");
      if (stored === "light" || stored === "dark") document.documentElement.dataset["theme"] = stored;
    } catch {
      // Privater Modus: Systemeinstellung.
    }
  }, []);

  const toggle = () => {
    const next = theme === "dark" ? "light" : "dark";
    setTheme(next);
    document.documentElement.dataset["theme"] = next;
    try {
      localStorage.setItem("theme", next);
    } catch {
      // Privater Modus: gilt dann nur bis zum Neuladen.
    }
  };
  return [theme, toggle];
}
